import axios from "axios";
import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { splitMixedTamilWord, getTanglishForMixedWord } from "./tamilPhonetics";

const API_URL = "https://translation.digitalmoitech.com/translate";
const TAMIL_CHAR_REGEX = /[\u0B80-\u0BFF]/;
const TAMIL_MARK_REGEX = /[\u0BBE-\u0BCD\u0BD7]/;
const isForceTamilTarget = (el) =>
  el?.dataset.forceTamil === "true" || el?.classList.contains("force-tamil");
const TAMIL_LETTER_VARIANTS = {
  "\u0BA8": ["\u0BA8", "\u0BA3", "\u0BA9"],
  "\u0BA3": ["\u0BA8", "\u0BA3", "\u0BA9"],
  "\u0BA9": ["\u0BA8", "\u0BA3", "\u0BA9"],
  "\u0BB2": ["\u0BB2", "\u0BB3", "\u0BB4"],
  "\u0BB3": ["\u0BB2", "\u0BB3", "\u0BB4"],
  "\u0BB4": ["\u0BB2", "\u0BB3", "\u0BB4"],
  "\u0BB0": ["\u0BB0", "\u0BB1"],
  "\u0BB1": ["\u0BB0", "\u0BB1"],
};

const useGlobalInputListener = () => {
  const lastActionWasPaste = useRef(false);
  const requestIdRef = useRef(0);
  const helperRef = useRef(null);
    const activeBoxRef = useRef("suggestion");
  const historyHelperRef = useRef(null);
  const historyStateRef = useRef(null);
  const triggerRef = useRef(null);
  const suggestionStateRef = useRef(null);
  const suggestionTriggerStateRef = useRef(null);
  const pendingTranslationsRef = useRef(new Map());
  const activeEnglishBufferRef = useRef(new Map());
  const suggestionCacheRef = useRef(new Map());
  const suggestionSourceCacheRef = useRef(new Map());
  const englishOptOutRef = useRef(new Map());
  const fieldSuggestionStateRef = useRef(new Map());
  const debounceTimerRef = useRef(null);
  const isTranslatorEnabled = useSelector(
    (state) =>
      (state.activePage.typingMode || "tanglish") === "tanglish",
  );

  useEffect(() => {
    const pendingTranslations = pendingTranslationsRef.current;

    const getWordHistory = () => {
      try {
        const history = localStorage.getItem("tamilWordHistory");
        return history ? JSON.parse(history) : {};
      } catch {
        return {};
      }
    };

    const saveToWordHistory = (englishWord, tamilWord) => {
      if (!englishWord || !tamilWord) return;
      englishWord = englishWord.trim();
      tamilWord = tamilWord.trim();
      if (englishWord.length < 2 || tamilWord.length === 0) return;
      
      const history = getWordHistory();
      const lowerEnglish = englishWord.toLowerCase();
      
      if (!history[lowerEnglish]) {
        history[lowerEnglish] = {};
      }
      
      if (!history[lowerEnglish][tamilWord]) {
        history[lowerEnglish][tamilWord] = 0;
      }
      
      history[lowerEnglish][tamilWord] += 1;
      localStorage.setItem("tamilWordHistory", JSON.stringify(history));
    };

    const getHistorySuggestions = (prefix) => {
      if (!prefix) return [];
      const lowerPrefix = prefix.toLowerCase();
      const history = getWordHistory();
      
      const matchedKeys = Object.keys(history).filter(k => k.startsWith(lowerPrefix));
      let allWords = [];
      
      matchedKeys.forEach(key => {
        const tamilWords = history[key];
        Object.entries(tamilWords).forEach(([word, count]) => {
           allWords.push({ english: key, tamil: word, count });
        });
      });
      
      allWords.sort((a, b) => b.count - a.count);
      
      const uniqueWords = [];
      const result = [];
      for (const item of allWords) {
         if (!uniqueWords.includes(item.tamil)) {
             uniqueWords.push(item.tamil);
             result.push(item);
             if (result.length >= 5) break;
         }
      }
      return result;
    };

    const getCaretCoordinates = (target, position) => {
      if (!target || !document.body.contains(target)) return null;

      const style = window.getComputedStyle(target);
      const mirror = document.createElement("div");
      const propertiesToCopy = [
        "boxSizing",
        "width",
        "height",
        "overflowX",
        "overflowY",
        "borderTopWidth",
        "borderRightWidth",
        "borderBottomWidth",
        "borderLeftWidth",
        "paddingTop",
        "paddingRight",
        "paddingBottom",
        "paddingLeft",
        "fontStyle",
        "fontVariant",
        "fontWeight",
        "fontStretch",
        "fontSize",
        "fontSizeAdjust",
        "lineHeight",
        "fontFamily",
        "letterSpacing",
        "textTransform",
        "textIndent",
        "textAlign",
        "whiteSpace",
        "wordSpacing",
      ];

      mirror.style.position = "absolute";
      mirror.style.visibility = "hidden";
      mirror.style.pointerEvents = "none";
      mirror.style.top = "0";
      mirror.style.left = "0";

      propertiesToCopy.forEach((property) => {
        mirror.style[property] = style[property];
      });

      mirror.style.whiteSpace =
        target.tagName === "INPUT" ? "pre" : "pre-wrap";
      mirror.style.wordWrap = "break-word";

      mirror.textContent = target.value.slice(0, position);

      const marker = document.createElement("span");
      marker.textContent = target.value.slice(position) || ".";
      mirror.appendChild(marker);

      document.body.appendChild(mirror);

      const targetRect = target.getBoundingClientRect();
      const markerRect = marker.getBoundingClientRect();
      const mirrorRect = mirror.getBoundingClientRect();

      const left =
        targetRect.left -
        target.scrollLeft +
        (markerRect.left - mirrorRect.left);
      const top =
        targetRect.top -
        target.scrollTop +
        (markerRect.top - mirrorRect.top);

      mirror.remove();

      return {
        left,
        top,
        height: markerRect.height || parseFloat(style.lineHeight) || 20,
      };
    };

    const getCachedOriginalWord = (target, word) => {
      if (typeof getSuggestionSourceCache === "function") {
        return getSuggestionSourceCache(target)?.get(word) || "";
      }
      return "";
    };

    const getViewportMetrics = () => {
      const vv = window.visualViewport;
      if (vv) {
        return {
          left: vv.offsetLeft,
          top: vv.offsetTop,
          width: vv.width,
          height: vv.height,
        };
      }
      return {
        left: 0,
        top: 0,
        width: window.innerWidth,
        height: window.innerHeight,
      };
    };

    const positionFloatingHelper = (
      helper,
      target,
      replacementEnd = null,
      isHistory = false
    ) => {
      if (!helper || !target || !document.body.contains(target)) return;

      const targetRect = target.getBoundingClientRect();
      const viewport = getViewportMetrics();
      const isMobile = viewport.width <= 640;

      if (!helper.parentElement) {
        document.body.appendChild(helper);
      }

      const maxAllowedWidth = isMobile
        ? Math.min(320, viewport.width - 24)
        : 320;
      helper.style.maxWidth = `${maxAllowedWidth}px`;
      if (isMobile) {
        helper.style.minWidth = `${Math.min(200, maxAllowedWidth)}px`;
      } else {
        helper.style.minWidth = "220px";
      }

      const helperHeight = helper.offsetHeight || 200;
      const helperWidth = helper.offsetWidth || maxAllowedWidth;

      // Available vertical space strictly outside target input rect
      const spaceBelow =
        viewport.top + viewport.height - (targetRect.bottom + 6);
      const spaceAbove = targetRect.top - 6 - viewport.top;

      let top;
      if (spaceBelow >= helperHeight) {
        // Fits comfortably below input
        top = targetRect.bottom + 6;
        const maxH = Math.min(300, Math.max(120, spaceBelow - 10));
        helper.style.maxHeight = `${maxH}px`;
      } else if (spaceAbove >= helperHeight) {
        // Fits comfortably above input
        const maxH = Math.min(300, Math.max(120, spaceAbove - 10));
        helper.style.maxHeight = `${maxH}px`;
        const actualH = Math.min(helperHeight, maxH);
        top = targetRect.top - actualH - 6;
      } else if (spaceBelow >= spaceAbove && spaceBelow >= 90) {
        // More space below than above
        const maxH = Math.max(80, spaceBelow - 10);
        helper.style.maxHeight = `${maxH}px`;
        top = targetRect.bottom + 6;
      } else {
        // More space above
        const maxH = Math.max(80, spaceAbove - 10);
        helper.style.maxHeight = `${maxH}px`;
        const actualH = Math.min(helperHeight, maxH);
        top = targetRect.top - actualH - 6;
      }

      // Horizontal positioning
      let caretPoint = null;
      if (
        typeof getCaretCoordinates === "function" &&
        typeof replacementEnd === "number"
      ) {
        caretPoint = getCaretCoordinates(target, replacementEnd);
      }

      let left;
      if (isMobile) {
        left = Math.max(
          viewport.left + 12,
          Math.min(
            targetRect.left,
            viewport.left + viewport.width - helperWidth - 12
          )
        );
      } else {
        const preferredLeft = caretPoint ? caretPoint.left - 8 : targetRect.left;
        left = Math.max(
          viewport.left + 12,
          Math.min(
            preferredLeft,
            viewport.left + viewport.width - helperWidth - 12
          )
        );
      }

      if (
        isHistory &&
        !isMobile &&
        helperRef.current &&
        helperRef.current.parentElement
      ) {
        const suggRect = helperRef.current.getBoundingClientRect();
        if (suggRect.width > 0) {
          if (
            suggRect.right + helperWidth + 12 <=
            viewport.left + viewport.width
          ) {
            left = suggRect.right + 4;
            top = suggRect.top;
          } else if (suggRect.left - helperWidth - 4 >= viewport.left + 12) {
            left = suggRect.left - helperWidth - 4;
            top = suggRect.top;
          }
        }
      }

      helper.style.left = `${left}px`;
      helper.style.top = `${top}px`;
    };

    const placeTriggerElement = (trigger, target, replacementEnd) => {
      if (!trigger || !target || !document.body.contains(target)) return;
      const targetRect = target.getBoundingClientRect();
      const viewport = getViewportMetrics();
      const isMobile = viewport.width <= 640;

      let left, top;
      if (isMobile) {
        left = Math.min(
          targetRect.right - 54,
          viewport.left + viewport.width - 58
        );
        top = Math.max(
          viewport.top + 4,
          targetRect.top + Math.max(2, (targetRect.height - 24) / 2)
        );
      } else {
        const caretPoint =
          typeof getCaretCoordinates === "function"
            ? (getCaretCoordinates(target, replacementEnd) ?? {
                left: targetRect.right - 70,
                top: targetRect.top,
                height: 20,
              })
            : {
                left: targetRect.right - 70,
                top: targetRect.top,
                height: 20,
              };
        left = Math.min(
          Math.max(viewport.left + 12, caretPoint.left + 8),
          viewport.left + viewport.width - 72
        );
        top = Math.min(
          Math.max(viewport.top + 4, caretPoint.top - 2),
          viewport.top + viewport.height - 36
        );
      }

      trigger.style.left = `${left}px`;
      trigger.style.top = `${top}px`;
    };

    const repositionAllHelpers = () => {
      if (helperRef.current && suggestionStateRef.current?.target) {
        positionFloatingHelper(
          helperRef.current,
          suggestionStateRef.current.target,
          suggestionStateRef.current.replacementEnd,
          false
        );
      }
      if (historyHelperRef.current && historyStateRef.current?.target) {
        positionFloatingHelper(
          historyHelperRef.current,
          historyStateRef.current.target,
          historyStateRef.current.replacementEnd,
          true
        );
      }
      if (triggerRef.current && suggestionTriggerStateRef.current?.target) {
        placeTriggerElement(
          triggerRef.current,
          suggestionTriggerStateRef.current.target,
          suggestionTriggerStateRef.current.replacementEnd
        );
      }
    };

    const removeHistoryHelper = () => {
      if (historyHelperRef.current) {
        historyHelperRef.current.remove();
        historyHelperRef.current = null;
      }
      historyStateRef.current = null;
    };

    const applyHistorySuggestion = ({ target, replacementStart, replacementEnd, suggestion }) => {
      const textAfter = target.value.slice(replacementEnd);
      const hasTrailingSpace = /^\s/.test(textAfter);
      const spaceToInsert = hasTrailingSpace ? "" : " ";

      const updatedValue =
        target.value.slice(0, replacementStart) +
        suggestion +
        spaceToInsert +
        textAfter;
        
      // Delete buffer BEFORE setting value so handleInput ignores the space!
      activeEnglishBufferRef.current?.delete(target);
      
      setNativeValue(target, updatedValue);
      
      const newCursorPos = replacementStart + suggestion.length + 1;
      
      requestAnimationFrame(() => {
        try {
          target.focus?.();
          target.setSelectionRange(newCursorPos, newCursorPos);
        } catch (e) {}
      });
      
      removeSuggestionHelper();
      removeSuggestionTrigger();
      removeHistoryHelper();
    };

    const showHistoryHelper = ({ target, prefix, replacementStart, replacementEnd }) => {
      if (
        target?.dataset?.suppressTranslate === "true" ||
        target?.classList?.contains("suppress-translate")
      ) {
        removeHistoryHelper();
        return;
      }
      const suggestions = getHistorySuggestions(prefix);
      if (suggestions.length === 0) {
        removeHistoryHelper();
        return;
      }

      const viewport = getViewportMetrics();
      const isMobile = viewport.width <= 640;

      // On mobile screens, don't show the secondary history box if transliteration suggestions are open
      if (isMobile && helperRef.current && helperRef.current.parentElement) {
        removeHistoryHelper();
        return;
      }

      removeHistoryHelper();

      historyStateRef.current = {
        target,
        suggestions,
        replacementStart,
        replacementEnd,
        selectedIndex: 0,
      };

      const helper = document.createElement("div");
      helper.id = "tamil-history-helper";
      helper.style.position = "fixed";
      helper.style.zIndex = "9998";
      helper.style.overflowY = "auto";
      helper.style.padding = isMobile ? "8px" : "10px";
      helper.style.borderRadius = "12px";
      helper.style.background = "#fdf8f5";
      helper.style.border = "1px solid #fed7aa";
      helper.style.boxShadow = "0 16px 40px rgba(15, 23, 42, 0.16)";
      helper.style.fontFamily = "Inter, system-ui, sans-serif";

      const label = document.createElement("div");
      label.textContent = "Word History";
      label.style.fontSize = isMobile ? "11px" : "12px";
      label.style.fontWeight = "700";
      label.style.color = "#c2410c";
      label.style.marginBottom = isMobile ? "4px" : "8px";
      helper.appendChild(label);

      const buttons = [];

      suggestions.forEach((item, index) => {
        const button = document.createElement("button");
        button.type = "button";
        button.style.display = "flex";
        button.style.alignItems = "center";
        button.style.gap = isMobile ? "6px" : "8px";
        button.style.width = "100%";
        button.style.marginTop = index === 0 ? "0" : (isMobile ? "4px" : "6px");
        button.style.padding = isMobile ? "6px 8px" : "8px 10px";
        button.style.border = "1px solid #ffedd5";
        button.style.borderRadius = "10px";
        button.style.background = index === 0 ? "#fff7ed" : "#fff";
        button.style.color = "#142235";
        button.style.cursor = "pointer";
        button.style.textAlign = "left";
        button.style.transition = "all 0.15s ease";

        const badge = document.createElement("span");
        badge.textContent = isMobile ? `${index + 1}` : `Ctrl+${index + 1}`;
        badge.style.display = "inline-flex";
        badge.style.alignItems = "center";
        badge.style.justifyContent = "center";
        badge.style.padding = "0 6px";
        badge.style.height = isMobile ? "18px" : "20px";
        badge.style.borderRadius = "999px";
        badge.style.background = "#ea580c";
        badge.style.color = "#ffffff";
        badge.style.fontSize = "10px";
        badge.style.fontWeight = "700";
        badge.style.flexShrink = "0";

        const text = document.createElement("span");
        text.textContent = item.tamil;
        text.style.fontSize = isMobile ? "13px" : "14px";
        text.style.wordBreak = "break-word";
        
        const engText = document.createElement("span");
        engText.textContent = `(${item.english})`;
        engText.style.fontSize = "11px";
        engText.style.color = "#94a3b8";
        engText.style.marginLeft = "auto";

        button.appendChild(badge);
        button.appendChild(text);
        button.appendChild(engText);

        button.addEventListener("mousedown", (event) => event.preventDefault());
        button.addEventListener("click", () => {
          applyHistorySuggestion({
            target,
            replacementStart: historyStateRef.current.replacementStart,
            replacementEnd: historyStateRef.current.replacementEnd,
            suggestion: item.tamil,
          });
        });
        
        button.addEventListener("mouseenter", () => {
          if (historyStateRef.current) {
            historyStateRef.current.selectedIndex = index;
            updateHighlight();
          }
        });

        helper.appendChild(button);
        buttons.push(button);
      });

      const updateHighlight = () => {
        if (!historyStateRef.current) return;
        const selectedIdx = historyStateRef.current.selectedIndex;
        buttons.forEach((btn, idx) => {
          if (idx === selectedIdx) {
            btn.style.background = "#fff7ed";
            btn.style.borderColor = "#ea580c";
            btn.style.fontWeight = "600";
          } else {
            btn.style.background = "#fff";
            btn.style.borderColor = "#ffedd5";
            btn.style.fontWeight = "400";
          }
        });
      };

      historyStateRef.current.buttons = buttons;
      historyStateRef.current.updateHighlight = updateHighlight;

      document.body.appendChild(helper);
      positionFloatingHelper(helper, target, replacementEnd, true);
      historyHelperRef.current = helper;
    };


    const isValidTarget = (el) => {
      if (!el) return false;

      const tag = el.tagName;
      const isPasswordField =
        tag === "INPUT" &&
        (el.type === "password" ||
          String(el.name || "").toLowerCase().includes("password") ||
          String(el.id || "").toLowerCase().includes("password") ||
          String(el.autocomplete || "").toLowerCase().includes("password"));
      const forceTamil = isForceTamilTarget(el);

      return (
        (tag === "INPUT" || tag === "TEXTAREA") &&
        (isTranslatorEnabled || forceTamil) &&
        !isPasswordField &&
        !el.readOnly &&
        !el.disabled &&
        !el.isContentEditable &&
        !el.classList.contains("no-translate")
      );
    };

    const setNativeValue = (target, value) => {
      const oldValue = target.value;
      let prototype = Object.getPrototypeOf(target);
      let setter;

      while (prototype) {
        const desc = Object.getOwnPropertyDescriptor(prototype, "value");
        if (desc && desc.set) {
          setter = desc.set;
          break;
        }
        prototype = Object.getPrototypeOf(prototype);
      }

      if (setter) {
        setter.call(target, value);
      } else {
        target.value = value;
      }

      const tracker = target._valueTracker;
      if (tracker) {
        tracker.setValue(oldValue);
      }

      target.dispatchEvent(
        new CustomEvent("input", {
          bubbles: true,
          detail: { isTranslation: true },
        }),
      );
    };

    const isTamilCharacter = (char) =>
      Boolean(char) && TAMIL_CHAR_REGEX.test(char);
    const isTamilMark = (char) => Boolean(char) && TAMIL_MARK_REGEX.test(char);
    const isDelimiter = (char) => !char || /[\s,!?\n-]/.test(char);
    const isEnglishToken = (value) =>
      /^[a-zA-Z0-9]+(?:[./&+'][a-zA-Z0-9]+)*$/.test(String(value || ""));
    const hasDeferredSymbol = (value) => /[./&+']/.test(String(value || ""));

    const getPendingTranslations = (target) =>
      pendingTranslationsRef.current.get(target) ?? [];

    const setPendingTranslations = (target, entries) => {
      if (entries.length) {
        pendingTranslationsRef.current.set(target, entries);
      } else {
        pendingTranslationsRef.current.delete(target);
      }
    };

    const addPendingTranslation = (target, entry) => {
      const entries = getPendingTranslations(target);
      setPendingTranslations(target, [...entries, entry]);
    };

    const removePendingTranslation = (target, entryId) => {
      const entries = getPendingTranslations(target).filter(
        (entry) => entry.id !== entryId,
      );
      setPendingTranslations(target, entries);
    };

    const shiftPendingTranslations = (
      target,
      fromIndex,
      delta,
      excludedEntryId,
    ) => {
      if (!delta) return;

      const entries = getPendingTranslations(target).map((entry) => {
        if (entry.id === excludedEntryId || entry.start < fromIndex) {
          return entry;
        }

        return {
          ...entry,
          start: entry.start + delta,
          end: entry.end + delta,
        };
      });

      setPendingTranslations(target, entries);
    };

    const removeSuggestionHelper = () => {
      helperRef.current?.remove();
      helperRef.current = null;
      suggestionStateRef.current = null;
    };

    const removeSuggestionTrigger = () => {
      triggerRef.current?.remove();
      triggerRef.current = null;
      suggestionTriggerStateRef.current = null;
    };

    const getSuggestionCacheKey = (target) =>
      [
        target?.form?.getAttribute("action") || window.location.pathname,
        target?.name || "",
        target?.id || "",
        target?.placeholder || "",
        target?.type || "",
      ].join("::");

    const getSuggestionCache = (target) => {
      const cacheKey = getSuggestionCacheKey(target);
      let cache = suggestionCacheRef.current.get(cacheKey);
      if (!cache) {
        cache = new Map();
        suggestionCacheRef.current.set(cacheKey, cache);
      }
      return cache;
    };

    const getEnglishOptOutSet = (target) => {
      const cacheKey = getSuggestionCacheKey(target);
      let cache = englishOptOutRef.current.get(cacheKey);
      if (!cache) {
        cache = new Set();
        englishOptOutRef.current.set(cacheKey, cache);
      }
      return cache;
    };

    const getEnglishOptOutKey = (start, end, word) => `${start}:${end}:${word}`;
    const getEnglishWordOptOutKey = (word) =>
      `word:${String(word).toLowerCase()}`;

    const setEnglishOptOut = (target, start, end, word) => {
      if (!word) return;
      const optOutSet = getEnglishOptOutSet(target);
      optOutSet.add(getEnglishOptOutKey(start, end, word));
      optOutSet.add(getEnglishWordOptOutKey(word));
      optOutSet.add(String(word).toLowerCase());
      if (target) {
        if (!target._englishOptOutWords) target._englishOptOutWords = new Set();
        target._englishOptOutWords.add(String(word).toLowerCase());
        target._englishOptOutWords.add(getEnglishOptOutKey(start, end, word));
      }
    };

    const clearEnglishOptOut = (target, start, end, word) => {
      if (!word) return;
      const optOutSet = getEnglishOptOutSet(target);
      optOutSet.delete(getEnglishOptOutKey(start, end, word));
      optOutSet.delete(getEnglishWordOptOutKey(word));
      optOutSet.delete(String(word).toLowerCase());
      if (target?._englishOptOutWords) {
        target._englishOptOutWords.delete(String(word).toLowerCase());
        target._englishOptOutWords.delete(getEnglishOptOutKey(start, end, word));
      }
    };

    const isEnglishOptedOut = (target, start, end, word) => {
      if (!word) return false;
      const optOutSet = getEnglishOptOutSet(target);
      const lower = String(word).toLowerCase();
      const posKey = getEnglishOptOutKey(start, end, word);
      const wordKey = getEnglishWordOptOutKey(word);
      return (
        optOutSet.has(posKey) ||
        optOutSet.has(wordKey) ||
        optOutSet.has(lower) ||
        Boolean(target?._englishOptOutWords?.has(lower)) ||
        Boolean(target?._englishOptOutWords?.has(posKey))
      );
    };

    const getSuggestionSourceCache = (target) => {
      const cacheKey = getSuggestionCacheKey(target);
      let cache = suggestionSourceCacheRef.current.get(cacheKey);
      if (!cache) {
        cache = new Map();
        suggestionSourceCacheRef.current.set(cacheKey, cache);
      }
      return cache;
    };



    const cacheSuggestions = (target, word, suggestions, originalWord) => {
      const uniqueSuggestions = [...new Set(suggestions)].filter(Boolean);
      if (!word || uniqueSuggestions.length <= 1) return;
      const cachedSuggestions = uniqueSuggestions.slice(0, 5);
      const targetCache = getSuggestionCache(target);
      const cacheKey = getSuggestionCacheKey(target);
      const sourceCache = getSuggestionSourceCache(target);

      targetCache.set(word, cachedSuggestions);
      cachedSuggestions.forEach((suggestion) => {
        targetCache.set(suggestion, cachedSuggestions);
        if (originalWord) {
          sourceCache.set(suggestion, originalWord);
        }
      });
      if (originalWord) {
        sourceCache.set(word, originalWord);
      }
      fieldSuggestionStateRef.current.set(cacheKey, cachedSuggestions);
    };

    const buildSuggestionList = (primarySuggestion, suggestions = []) => {
      const uniqueSuggestions = [
        primarySuggestion,
        ...suggestions,
      ].filter(Boolean);
      return [...new Set(uniqueSuggestions)].slice(0, 5);
    };

    const isTargetStillActive = (target) => document.activeElement === target;



    const getWordRangeAtCursor = (value, cursorPos) => {
      if (!value) return null;

      let anchorIndex = -1;
      const charBeforeCursor = value[cursorPos - 1];
      const charAtCursor = value[cursorPos];

      if (!isDelimiter(charBeforeCursor)) {
        anchorIndex = cursorPos - 1;
      } else if (!isDelimiter(charAtCursor)) {
        anchorIndex = cursorPos;
      }

      if (anchorIndex < 0) return null;

      let start = anchorIndex;
      let end = anchorIndex + 1;

      while (start > 0 && !isDelimiter(value[start - 1])) {
        start -= 1;
      }

      while (end < value.length && !isDelimiter(value[end])) {
        end += 1;
      }

      return {
        start,
        end,
        word: value.slice(start, end),
      };
    };

    const getTamilLetterAtCursor = (value, cursorPos) => {
      if (!value) return null;

      const charBeforeCursor = value[cursorPos - 1];
      const charAtCursor = value[cursorPos];

      let anchorIndex = -1;

      if (isTamilCharacter(charBeforeCursor)) {
        anchorIndex = cursorPos - 1;
      } else if (isTamilCharacter(charAtCursor)) {
        anchorIndex = cursorPos;
      }

      if (anchorIndex < 0) return null;

      let clusterStart = anchorIndex;

      while (clusterStart > 0 && isTamilMark(value[clusterStart])) {
        clusterStart -= 1;
      }

      const baseLetter = value[clusterStart];
      const variants = TAMIL_LETTER_VARIANTS[baseLetter];

      if (!variants) return null;

      let clusterEnd = clusterStart + 1;

      while (clusterEnd < value.length && isTamilMark(value[clusterEnd])) {
        clusterEnd += 1;
      }

      return {
        baseLetter,
        clusterStart,
        clusterEnd,
        suffix: value.slice(clusterStart + 1, clusterEnd),
        variants,
      };
    };

    const replaceTamilLetterVariant = (target, nextLetter) => {
      if (!isValidTarget(target)) return false;

      const selectionStart = target.selectionStart ?? 0;
      const selectionEnd = target.selectionEnd ?? selectionStart;
      const tamilLetter = getTamilLetterAtCursor(target.value, selectionStart);

      if (!tamilLetter || tamilLetter.baseLetter === nextLetter) return false;

      const updatedValue =
        target.value.slice(0, tamilLetter.clusterStart) +
        nextLetter +
        tamilLetter.suffix +
        target.value.slice(tamilLetter.clusterEnd);

      setNativeValue(target, updatedValue);

      requestAnimationFrame(() => {
        try {
          target.focus?.();
          target.setSelectionRange(selectionStart, selectionEnd);
        } catch (e) {}
      });

      return true;
    };

    const cycleTamilLetterVariant = (target) => {
      const selectionStart = target.selectionStart ?? 0;
      const tamilLetter = getTamilLetterAtCursor(target.value, selectionStart);

      if (!tamilLetter) return false;

      const currentIndex = tamilLetter.variants.indexOf(tamilLetter.baseLetter);
      const nextLetter =
        tamilLetter.variants[(currentIndex + 1) % tamilLetter.variants.length];

      return replaceTamilLetterVariant(target, nextLetter);
    };

    const applySuggestion = ({
      target,
      replacementStart,
      replacementEnd,
      suggestion,
      suggestions,
      originalWord,
    }) => {
      if (!isValidTarget(target)) {
        removeSuggestionHelper();
        return;
      }

      const currentWord = target.value.slice(replacementStart, replacementEnd);
      if (!currentWord) {
        removeSuggestionHelper();
        return;
      }

      const delta = suggestion.length - (replacementEnd - replacementStart);
      const resolvedOriginalWord =
        originalWord || suggestionStateRef.current?.originalWord;
      const isContinuingWithEnglish =
        Boolean(resolvedOriginalWord) && suggestion === resolvedOriginalWord;

      clearEnglishOptOut(target, replacementStart, replacementEnd, currentWord);
      if (isContinuingWithEnglish) {
        setEnglishOptOut(
          target,
          replacementStart,
          replacementStart + suggestion.length,
          suggestion,
        );
      } else {
        clearEnglishOptOut(
          target,
          replacementStart,
          replacementStart + suggestion.length,
          suggestion,
        );
        clearEnglishOptOut(
          target,
          replacementStart,
          replacementStart + suggestion.length,
          resolvedOriginalWord,
        );
      }

      const textAfter = target.value.slice(replacementEnd);
      const hasTrailingSpace = /^\s/.test(textAfter);
      const spaceToInsert = hasTrailingSpace ? "" : " ";

      const updatedValue =
        target.value.slice(0, replacementStart) +
        suggestion +
        spaceToInsert +
        textAfter;

      activeEnglishBufferRef.current?.delete(target);
      setNativeValue(target, updatedValue);
      saveToWordHistory(typeof currentWord !== "undefined" ? currentWord : currentWord, suggestion);
      shiftPendingTranslations(target, replacementEnd, delta + spaceToInsert.length);
      cacheSuggestions(
        target,
        suggestion,
        suggestions || suggestionStateRef.current?.suggestions || [],
        resolvedOriginalWord,
      );

      const newCursorPos = replacementStart + suggestion.length + 1;

      requestAnimationFrame(() => {
        if (!isTargetStillActive(target)) return;

        try {
          target.focus?.();
          target.setSelectionRange(newCursorPos, newCursorPos);
        } catch (e) {}
      });

      removeSuggestionHelper();
      removeSuggestionTrigger();
      removeHistoryHelper();
    };

    const showSuggestionHelper = ({
      target,
      suggestions,
      replacementStart,
      replacementEnd,
      originalWord,
      force = false,
    }) => {
      const uniqueSuggestions = [...new Set(suggestions)].filter(Boolean);
      const currentWord = target.value.slice(replacementStart, replacementEnd);
      const resolvedOriginalWord =
        originalWord ||
        getCachedOriginalWord(target, currentWord) ||
        (isEnglishToken(currentWord) ? currentWord : "");
      const includeOriginalOption = Boolean(resolvedOriginalWord);

      if (
        target?.dataset?.suppressTranslate === "true" ||
        target?.classList?.contains("suppress-translate") ||
        (!includeOriginalOption && uniqueSuggestions.length <= 1) ||
        document.activeElement !== target ||
        (!force && !uniqueSuggestions.includes(currentWord))
      ) {
        removeSuggestionHelper();
        return;
      }

      removeSuggestionHelper();

      const allSuggestions = uniqueSuggestions.slice(0, 5);

      suggestionStateRef.current = {
        target,
        suggestions: allSuggestions,
        replacementStart,
        replacementEnd,
        originalWord: resolvedOriginalWord,
        selectedIndex: 0,
        includeOriginalOption,
      };

      const viewport = getViewportMetrics();
      const isMobile = viewport.width <= 640;

      const helper = document.createElement("div");
      helper.id = "tamil-typing-helper";
      helper.style.position = "fixed";
      helper.style.zIndex = "9999";
      helper.style.overflowY = "auto";
      helper.style.padding = isMobile ? "8px" : "10px";
      helper.style.borderRadius = "12px";
      helper.style.background = "#ffffff";
      helper.style.border = "1px solid #dbe5ec";
      helper.style.boxShadow = "0 16px 40px rgba(15, 23, 42, 0.16)";
      helper.style.fontFamily = "Inter, system-ui, sans-serif";

      const label = document.createElement("div");
      label.textContent = "Tamil suggestions";
      label.style.fontSize = isMobile ? "11px" : "12px";
      label.style.fontWeight = "700";
      label.style.color = "#5f7187";
      label.style.marginBottom = isMobile ? "4px" : "8px";
      helper.appendChild(label);

      const buttons = [];

      allSuggestions.forEach((suggestion, index) => {
        const button = document.createElement("button");
        button.type = "button";
        button.setAttribute("data-index", index);
        button.style.display = "flex";
        button.style.alignItems = "center";
        button.style.gap = isMobile ? "6px" : "8px";
        button.style.width = "100%";
        button.style.marginTop = index === 0 ? "0" : (isMobile ? "4px" : "6px");
        button.style.padding = isMobile ? "6px 8px" : "8px 10px";
        button.style.border = "1px solid #e2e8f0";
        button.style.borderRadius = "10px";
        button.style.background = index === 0 ? "#f0fdfa" : "#f8fafc";
        button.style.color = "#142235";
        button.style.cursor = "pointer";
        button.style.textAlign = "left";
        button.style.transition = "all 0.15s ease";

        const badge = document.createElement("span");
        badge.textContent = `${index + 1}`;
        badge.style.display = "inline-flex";
        badge.style.alignItems = "center";
        badge.style.justifyContent = "center";
        badge.style.width = isMobile ? "18px" : "20px";
        badge.style.height = isMobile ? "18px" : "20px";
        badge.style.borderRadius = "999px";
        badge.style.background = "#0f766e";
        badge.style.color = "#ffffff";
        badge.style.fontSize = isMobile ? "10px" : "11px";
        badge.style.fontWeight = "700";
        badge.style.flexShrink = "0";

        const text = document.createElement("span");
        text.textContent = suggestion;
        text.style.fontSize = isMobile ? "13px" : "14px";
        text.style.wordBreak = "break-word";

        button.appendChild(badge);
        button.appendChild(text);
        button.addEventListener("mousedown", (event) => event.preventDefault());
        button.addEventListener("click", () => {
          applySuggestion({
            target,
            replacementStart,
            replacementEnd,
            suggestion,
            suggestions: suggestionStateRef.current?.suggestions || [],
          });
        });
        button.addEventListener("mouseenter", () => {
          if (suggestionStateRef.current) {
            suggestionStateRef.current.selectedIndex = index;
            updateHighlight();
          }
        });
        
        helper.appendChild(button);
        buttons.push(button);
      });

      if (includeOriginalOption) {
        const englishButton = document.createElement("button");
        englishButton.type = "button";
        englishButton.setAttribute("data-index", allSuggestions.length);
        englishButton.style.display = "flex";
        englishButton.style.alignItems = "center";
        englishButton.style.gap = isMobile ? "6px" : "8px";
        englishButton.style.width = "100%";
        englishButton.style.marginTop = isMobile ? "4px" : "6px";
        englishButton.style.padding = isMobile ? "6px 8px" : "8px 10px";
        englishButton.style.border = "1px dashed #cbd5e1";
        englishButton.style.borderRadius = "10px";
        englishButton.style.background = "#f8fafc";
        englishButton.style.color = "#142235";
        englishButton.style.cursor = "pointer";
        englishButton.style.textAlign = "left";
        englishButton.style.transition = "all 0.15s ease";

        const englishBadge = document.createElement("span");
        englishBadge.textContent = "E";
        englishBadge.style.display = "inline-flex";
        englishBadge.style.alignItems = "center";
        englishBadge.style.justifyContent = "center";
        englishBadge.style.width = isMobile ? "18px" : "20px";
        englishBadge.style.height = isMobile ? "18px" : "20px";
        englishBadge.style.borderRadius = "999px";
        englishBadge.style.background = "#64748b";
        englishBadge.style.color = "#ffffff";
        englishBadge.style.fontSize = isMobile ? "10px" : "11px";
        englishBadge.style.fontWeight = "700";
        englishBadge.style.flexShrink = "0";

        const englishText = document.createElement("span");
        englishText.textContent = `Continue with English: ${resolvedOriginalWord}`;
        englishText.style.fontSize = isMobile ? "12px" : "14px";
        englishText.style.color = "#6b7280";
        englishText.style.wordBreak = "break-word";
        englishText.style.flex = "1";

        const englishShortcutBadge = document.createElement("span");
        englishShortcutBadge.textContent = "Shift+↵";
        englishShortcutBadge.style.display = isMobile ? "none" : "inline-flex";
        englishShortcutBadge.style.alignItems = "center";
        englishShortcutBadge.style.justifyContent = "center";
        englishShortcutBadge.style.padding = "2px 6px";
        englishShortcutBadge.style.borderRadius = "4px";
        englishShortcutBadge.style.background = "#e2e8f0";
        englishShortcutBadge.style.color = "#475569";
        englishShortcutBadge.style.fontSize = "11px";
        englishShortcutBadge.style.fontWeight = "600";
        englishShortcutBadge.style.marginLeft = "auto";
        englishShortcutBadge.style.flexShrink = "0";
        englishShortcutBadge.title = "Shortcuts: Shift+Enter, Alt+E, or Ctrl+Enter";

        englishButton.appendChild(englishBadge);
        englishButton.appendChild(englishText);
        englishButton.appendChild(englishShortcutBadge);
        englishButton.addEventListener("mousedown", (event) => event.preventDefault());
        englishButton.addEventListener("click", () => {
          activeEnglishBufferRef.current?.delete(target);
          applySuggestion({
            target,
            replacementStart,
            replacementEnd,
            suggestion: resolvedOriginalWord,
            suggestions: suggestionStateRef.current?.suggestions || [],
            originalWord: resolvedOriginalWord,
          });
        });
        englishButton.addEventListener("mouseenter", () => {
          if (suggestionStateRef.current) {
            suggestionStateRef.current.selectedIndex = allSuggestions.length;
            updateHighlight();
          }
        });

        helper.appendChild(englishButton);
        buttons.push(englishButton);
      }

      const updateHighlight = () => {
        if (!suggestionStateRef.current) return;
        const selectedIdx = suggestionStateRef.current.selectedIndex;
        buttons.forEach((btn, idx) => {
          if (idx === selectedIdx) {
            btn.style.background = "#f0fdfa";
            btn.style.borderColor = "#0f766e";
            btn.style.fontWeight = "600";
          } else {
            btn.style.background = "#f8fafc";
            btn.style.borderColor = "#e2e8f0";
            btn.style.fontWeight = "400";
          }
        });
      };

      suggestionStateRef.current.buttons = buttons;
      suggestionStateRef.current.updateHighlight = updateHighlight;

      const hint = document.createElement("div");
      if (isMobile) {
        hint.textContent = "Tap to select";
        hint.style.fontSize = "10px";
        hint.style.marginTop = "4px";
      } else {
        hint.textContent = "Use ↑↓ to navigate, Enter for Tamil, Shift+↵ or Alt+E for English, Alt+1-5 quick pick";
        hint.style.fontSize = "11px";
        hint.style.marginTop = "8px";
      }
      hint.style.color = "#7b8794";
      helper.appendChild(hint);

      document.body.appendChild(helper);
      positionFloatingHelper(helper, target, replacementEnd, false);
      helperRef.current = helper;
    };

    const showSuggestionTrigger = ({
      target,
      suggestions,
      replacementStart,
      replacementEnd,
      originalWord,
      force = false,
    }) => {
      const uniqueSuggestions = [...new Set(suggestions)].filter(Boolean);
      const currentWord = target.value.slice(replacementStart, replacementEnd);
      const resolvedOriginalWord =
        originalWord ||
        getCachedOriginalWord(target, currentWord) ||
        (isEnglishToken(currentWord) ? currentWord : "");
      const includeOriginalOption = Boolean(resolvedOriginalWord);

      if (
        target?.dataset?.suppressTranslate === "true" ||
        target?.classList?.contains("suppress-translate") ||
        (!includeOriginalOption && uniqueSuggestions.length <= 1) ||
        document.activeElement !== target ||
        (!force && !uniqueSuggestions.includes(currentWord))
      ) {
        removeSuggestionTrigger();
        return;
      }

      removeSuggestionTrigger();

      suggestionTriggerStateRef.current = {
        target,
        suggestions: uniqueSuggestions.slice(0, 5),
        replacementStart,
        replacementEnd,
      };

      const trigger = document.createElement("button");
      trigger.type = "button";
      trigger.setAttribute("aria-label", "Show Tamil suggestions");
      trigger.textContent = "Alt+J";
      trigger.style.position = "fixed";
      trigger.style.zIndex = "9998";
      trigger.style.display = "inline-flex";
      trigger.style.alignItems = "center";
      trigger.style.justifyContent = "center";
      trigger.style.height = "24px";
      trigger.style.padding = "0 10px";
      trigger.style.border = "1px solid #0f766e";
      trigger.style.borderRadius = "999px";
      trigger.style.background = "#ecfeff";
      trigger.style.color = "#115e59";
      trigger.style.fontSize = "11px";
      trigger.style.fontWeight = "700";
      trigger.style.boxShadow = "0 10px 25px rgba(15, 23, 42, 0.12)";
      trigger.style.cursor = "pointer";

      trigger.addEventListener("mousedown", (event) => event.preventDefault());
      trigger.addEventListener("click", () => {
        const triggerState = suggestionTriggerStateRef.current;
        if (!triggerState) return;

        showSuggestionHelper(triggerState);
      });

      document.body.appendChild(trigger);
      placeTriggerElement(trigger, target, replacementEnd);

      triggerRef.current = trigger;
    };

    const reopenSuggestionsAtCursor = (target) => {
      if (!isValidTarget(target)) return false;

      const targetCache = getSuggestionCache(target);
      const fieldSuggestions = fieldSuggestionStateRef.current.get(
        getSuggestionCacheKey(target),
      );
      const cursorPos = target.selectionStart ?? 0;
      const candidateRanges = [
        getWordRangeAtCursor(target.value, cursorPos),
        getWordRangeAtCursor(target.value, Math.max(0, cursorPos - 1)),
        getWordRangeAtCursor(
          target.value,
          Math.min(target.value.length, cursorPos + 1),
        ),
      ].filter(Boolean);

      const wordRange =
        candidateRanges.find((range) =>
          targetCache.has(range.word),
        ) || candidateRanges[0];

      if (!wordRange) return false;

      const suggestions =
        targetCache.get(wordRange.word) ||
        (/[^\u0000-\u007F]/.test(wordRange.word) ? fieldSuggestions : null);
      if (!suggestions || suggestions.length <= 1) return false;

      showSuggestionHelper({
        target,
        suggestions,
        replacementStart: wordRange.start,
        replacementEnd: wordRange.end,
        originalWord: getCachedOriginalWord(target, wordRange.word),
        force: !targetCache.has(wordRange.word),
      });
      showSuggestionTrigger({
        target,
        suggestions,
        replacementStart: wordRange.start,
        replacementEnd: wordRange.end,
      });
      return true;
    };

    const fetchSuggestionsForWord = async (word, target) => {
      if (!word || word.length < 1) return null;

      const mixed = splitMixedTamilWord(word);
      if (mixed) {
        const { leadingNonTamil, tamilPrefix, englishSuffix } = mixed;
        const tanglishWord = getTanglishForMixedWord(tamilPrefix, englishSuffix);
        const rawMixed = tamilPrefix + englishSuffix;

        try {
          const [tanglishRes, rawRes] = await Promise.all([
            axios
              .post(
                API_URL,
                { text: tanglishWord, source: "en", target: "ta", mode: "transliterate" },
                { headers: { "Content-Type": "application/json" } }
              )
              .catch(() => null),
            axios
              .post(
                API_URL,
                { text: rawMixed, source: "en", target: "ta", mode: "transliterate" },
                { headers: { "Content-Type": "application/json" } }
              )
              .catch(() => null),
          ]);

          const tanglishList = [
            tanglishRes?.data?.translated,
            ...(Array.isArray(tanglishRes?.data?.suggestions) ? tanglishRes.data.suggestions : []),
          ].filter(Boolean);

          const rawTranslated = rawRes?.data?.translated;
          const rawSuggestions = Array.isArray(rawRes?.data?.suggestions)
            ? rawRes.data.suggestions
            : [];

          const prefixedRaw = [];
          if (rawTranslated && rawTranslated.startsWith(tamilPrefix)) {
            prefixedRaw.push(rawTranslated);
          }
          for (const s of rawSuggestions) {
            if (s.startsWith(tamilPrefix)) {
              prefixedRaw.push(s);
            } else {
              prefixedRaw.push(tamilPrefix + s);
            }
          }

          const tanglishPrefixMatches = tanglishList.filter((s) => s.startsWith(tamilPrefix));
          const otherTanglish = tanglishList.filter((s) => !s.startsWith(tamilPrefix));

          const combined = Array.from(
            new Set([...tanglishPrefixMatches, ...prefixedRaw, ...otherTanglish])
          ).filter(Boolean);

          const fullSuggestions = leadingNonTamil
            ? combined.map((s) => leadingNonTamil + s)
            : combined;

          const preferredTranslation =
            fullSuggestions[0] ||
            (leadingNonTamil + (rawTranslated || tamilPrefix + englishSuffix));

          return {
            preferredTranslation,
            suggestions: fullSuggestions,
            tanglishWord,
            isMixed: true,
          };
        } catch (err) {
          console.error("Mixed word transliteration failed:", err);
          return null;
        }
      }

      // Pure English word
      try {
        const res = await axios.post(
          API_URL,
          { text: word, source: "en", target: "ta", mode: "transliterate" },
          { headers: { "Content-Type": "application/json" } }
        );

        const translated = res.data?.translated;
        const apiSuggestions = Array.isArray(res.data?.suggestions)
          ? res.data.suggestions
          : [];
        const preferredTranslation = apiSuggestions[0] || translated;
        const suggestions = buildSuggestionList(preferredTranslation, [
          translated,
          ...apiSuggestions,
        ]);

        return {
          preferredTranslation,
          suggestions,
          tanglishWord: word,
          isMixed: false,
        };
      } catch (err) {
        console.error("Transliteration failed:", err);
        return null;
      }
    };

    const translateWord = async (target, force = false) => {
      if (lastActionWasPaste.current) return;
      if (!isValidTarget(target)) return;

      const cursorPos = target.selectionEnd ?? 0;
      const textBeforeCursor = target.value.slice(0, cursorPos);
      const regex = force
        ? /([^\s,!?\n-]+)$/
        : /([^\s,!?\n-]+)([\s,!?\n-]+)$/;
      const match = textBeforeCursor.match(regex);

      if (!match) return;

      const word = match[1];
      const delimiter = (!force && match[2]) ? match[2] : "";
      const matchLength = match[0].length;
      const wordStart = cursorPos - matchLength;
      const wordEnd = wordStart + word.length;

      if (isEnglishOptedOut(target, wordStart, wordEnd, word)) {
        return;
      }

      const alreadyPending = getPendingTranslations(target).some(
        (entry) =>
          entry.word === word &&
          entry.start === wordStart &&
          entry.end === wordEnd,
      );
      if (alreadyPending) return;

      const entryId = ++requestIdRef.current;
      addPendingTranslation(target, {
        id: entryId,
        word,
        start: wordStart,
        end: wordEnd,
      });

      try {
        const result = await fetchSuggestionsForWord(word, target);

        const pendingEntry = getPendingTranslations(target).find(
          (entry) => entry.id === entryId,
        );
        if (!pendingEntry || !result) {
          removePendingTranslation(target, entryId);
          return;
        }

        const { preferredTranslation, suggestions } = result;

        if (!preferredTranslation || preferredTranslation === word) {
          removePendingTranslation(target, entryId);
          return;
        }

        const currentWord = target.value.slice(
          pendingEntry.start,
          pendingEntry.end,
        );
        if (currentWord !== pendingEntry.word) {
          removePendingTranslation(target, entryId);
          return;
        }

        const textAfter = target.value.slice(cursorPos);
        const hasTrailingSpace = /^\s/.test(textAfter);
        const spaceToInsert = force
          ? ""
          : (hasTrailingSpace ? "" : (delimiter || " "));

        const updatedValue =
          target.value.slice(0, pendingEntry.start) +
          preferredTranslation +
          spaceToInsert +
          textAfter;

        const nextSelectionPos =
          pendingEntry.start +
          preferredTranslation.length +
          (force ? 0 : (hasTrailingSpace ? 1 : spaceToInsert.length));

        const delta =
          preferredTranslation.length - (pendingEntry.end - pendingEntry.start);

        if (updatedValue !== target.value) {
          setNativeValue(target, updatedValue);
          saveToWordHistory(word, preferredTranslation);
          shiftPendingTranslations(target, pendingEntry.end, delta, entryId);
          cacheSuggestions(
            target,
            preferredTranslation,
            suggestions,
            pendingEntry.word,
          );

          requestAnimationFrame(() => {
            if (!isTargetStillActive(target)) return;

            try {
              target.focus?.();
              target.setSelectionRange(nextSelectionPos, nextSelectionPos);
            } catch (e) {}

            const suggestionOverlayState = {
              target,
              suggestions,
              replacementStart: pendingEntry.start,
              replacementEnd:
                pendingEntry.start + preferredTranslation.length,
              originalWord: pendingEntry.word,
              force: true,
            };

            activeBoxRef.current = "suggestion";
            showSuggestionHelper(suggestionOverlayState);
            showSuggestionTrigger(suggestionOverlayState);
          });
        }

        removePendingTranslation(target, entryId);
      } catch (err) {
        removePendingTranslation(target, entryId);
        console.error("Translation failed:", err.message);
      }
    };

    const translatePastedWord = async ({
      target,
      pastedText,
      pasteStart,
    }) => {
      const normalizedText = pastedText.trim();

      if (
        !normalizedText ||
        normalizedText.length < 2 ||
        !isEnglishToken(normalizedText)
      ) {
        return false;
      }

      try {
        const res = await axios.post(
          API_URL,
          {
            text: normalizedText,
            source: "en",
            target: "ta",
            mode: "transliterate",
          },
          { headers: { "Content-Type": "application/json" } },
        );

        const translated = res.data.translated;
        const apiSuggestions = Array.isArray(res.data.suggestions)
          ? res.data.suggestions
          : [];
        const preferredTranslation = apiSuggestions[0] || translated;
        const suggestions = buildSuggestionList(
          preferredTranslation,
          apiSuggestions,
        );

        if (!preferredTranslation) return false;

        const currentSegment = target.value.slice(
          pasteStart,
          pasteStart + pastedText.length,
        );
        if (currentSegment.trim() !== normalizedText) {
          return false;
        }

        const updatedValue =
          target.value.slice(0, pasteStart) +
          preferredTranslation +
          target.value.slice(pasteStart + pastedText.length);

        setNativeValue(target, updatedValue);
        cacheSuggestions(
          target,
          preferredTranslation,
          suggestions,
          normalizedText,
        );

        requestAnimationFrame(() => {
          if (!isTargetStillActive(target)) return;

          const selectionPos = pasteStart + preferredTranslation.length;
          try {
            target.focus?.();
            target.setSelectionRange(selectionPos, selectionPos);
          } catch (e) {}

          const suggestionOverlayState = {
            target,
            suggestions,
            replacementStart: pasteStart,
            replacementEnd: pasteStart + preferredTranslation.length,
            originalWord: normalizedText,
            force: true,
          };

          activeBoxRef.current = "suggestion";
            showSuggestionHelper(suggestionOverlayState);
          showSuggestionTrigger(suggestionOverlayState);
        });

        return true;
      } catch (err) {
        console.error("Paste translation failed:", err.message);
        return false;
      }
    };

    const translateWordFromBuffer = async (target, buffer) => {
      const { word, start } = buffer;
      if (word.length < 1) return;
      if (isEnglishOptedOut(target, start, start + word.length, word)) {
        removeSuggestionHelper();
        removeSuggestionTrigger();
        return;
      }

      const mixed = splitMixedTamilWord(word);
      const historyPrefix = mixed
        ? getTanglishForMixedWord(mixed.tamilPrefix, mixed.englishSuffix)
        : word;

      if (typeof showHistoryHelper !== "undefined") {
        showHistoryHelper({
          target,
          prefix: historyPrefix,
          replacementStart: start,
          replacementEnd: start + word.length,
        });
      }

      try {
        const result = await fetchSuggestionsForWord(word, target);
        if (!result) return;

        const { preferredTranslation, suggestions } = result;
        if (!preferredTranslation) return;

        const currentBuffer = activeEnglishBufferRef.current.get(target);
        if (!currentBuffer || currentBuffer.word !== word) {
          return;
        }

        cacheSuggestions(
          target,
          preferredTranslation,
          suggestions,
          word,
        );

        // DO NOT modify target.value inline while typing
        const newCursorPos = start + word.length;
        currentBuffer.tamilLength = word.length;
        currentBuffer.expectedCursorPos = newCursorPos;
        activeEnglishBufferRef.current.set(target, currentBuffer);

        const suggestionOverlayState = {
          target,
          suggestions,
          replacementStart: start,
          replacementEnd: newCursorPos,
          originalWord: word,
          force: true,
        };
        activeBoxRef.current = "suggestion";
        showSuggestionHelper(suggestionOverlayState);
        showSuggestionTrigger(suggestionOverlayState);

        showHistoryHelper({
          target,
          prefix: historyPrefix,
          replacementStart: start,
          replacementEnd: newCursorPos,
        });
      } catch (err) {
        console.error("Realtime translation error:", err);
      }
    };

    const handleInput = (e) => {
      const target = e.target;
      const prevHelperState = suggestionStateRef.current;

      if (!isValidTarget(target)) return;
      if (e.detail?.isTranslation) return;

      removeSuggestionHelper();
      removeSuggestionTrigger();

      const lastChar = target.value.slice(-1);
      const inputType = e.inputType;
      const data = e.data;
      const currentSelectionEnd = target.selectionEnd ?? target.value.length;
      const charBeforeCursor = target.value.slice(0, currentSelectionEnd).slice(-1);
      const isDelimiter = /[\s,!?\n-]/.test(data || charBeforeCursor || lastChar);

      if (
        inputType === "insertText" &&
        data === "." &&
        /[a-zA-Z0-9]$/.test(target.value.slice(0, -1))
      ) {
        activeEnglishBufferRef.current.delete(target);
        removeHistoryHelper();
        return;
      }

      if (isDelimiter) {
        let buffer = activeEnglishBufferRef.current?.get(target);
        if (buffer && buffer.word && buffer.tamilLength > 0) {
            const tamilWord = target.value.slice(buffer.start, buffer.start + buffer.tamilLength);
            saveToWordHistory(buffer.word, tamilWord);
        }
        activeEnglishBufferRef.current?.delete(target);
        if (typeof removeHistoryHelper !== "undefined") removeHistoryHelper();
        
        if (prevHelperState && prevHelperState.target === target) {
            const selectedIndex = prevHelperState.selectedIndex ?? 0;
            if (selectedIndex !== -1) {
                const suggestion = prevHelperState.suggestions[selectedIndex];
                const finalSuggestion = suggestion || (prevHelperState.includeOriginalOption ? prevHelperState.originalWord : "");
                
                if (finalSuggestion) {
                    applySuggestion({
                        target,
                        replacementStart: prevHelperState.replacementStart,
                        replacementEnd: prevHelperState.replacementEnd,
                        suggestion: finalSuggestion,
                        suggestions: prevHelperState.suggestions,
                        originalWord: prevHelperState.originalWord
                    });
                    
                    removeSuggestionHelper();
                    removeSuggestionTrigger();
                    return;
                }
            }
        }
        
        translateWord(target, false);
        
        removeSuggestionHelper();
        removeSuggestionTrigger();
        return;
      }

      const selectionEnd = target.selectionEnd ?? 0;

      const updateBufferFromCursor = () => {
          const textBefore = target.value.slice(0, selectionEnd);
          const textAfter = target.value.slice(selectionEnd);
          
          // Use word boundaries (spaces/punctuation) instead of just English letters.
          // This captures mixed Tamil+English words as a single unit so that
          // editing a letter in the middle of an already-translated Tamil word
          // sends the WHOLE word for re-translation.
          const leftMatch = textBefore.match(/[^\s,!?\n-]+$/);
          const rightMatch = textAfter.match(/^[^\s,!?\n-]+/);
          
          const start = leftMatch ? selectionEnd - leftMatch[0].length : selectionEnd;
          const end = rightMatch ? selectionEnd + rightMatch[0].length : selectionEnd;
          
          const fullWord = target.value.slice(start, end);
          
          // Only proceed if the word contains at least one English letter
          const hasEnglish = /[a-zA-Z]/.test(fullWord);
          if (!hasEnglish || fullWord.length === 0) {
              activeEnglishBufferRef.current.delete(target);
              return null;
          }
          
          const newBuffer = {
              word: fullWord,
              start,
              tamilLength: fullWord.length,
              expectedCursorPos: selectionEnd
          };
          activeEnglishBufferRef.current.set(target, newBuffer);
          return newBuffer;
      };

      if (inputType === "deleteContentBackward" || inputType === "deleteContentForward") {
          const newBuffer = updateBufferFromCursor();
          if (newBuffer && /[a-zA-Z]/.test(newBuffer.word) && !hasDeferredSymbol(newBuffer.word)) {
              translateWordFromBuffer(target, newBuffer);
          } else {
              removeSuggestionHelper();
              removeSuggestionTrigger();
              removeHistoryHelper();
          }
          return;
      }

      if (inputType === "insertText" && /[a-zA-Z']/.test(data || lastChar)) {
          const newBuffer = updateBufferFromCursor();
          if (newBuffer && !hasDeferredSymbol(newBuffer.word)) {
              translateWordFromBuffer(target, newBuffer);
          }
      } else if (inputType === "insertText") {
          const newBuffer = updateBufferFromCursor();
          if (newBuffer && hasDeferredSymbol(newBuffer.word)) return;
          translateWord(target, true);
      }
    };

    const handlePaste = (e) => {
      const target = e.target;

      if (!isValidTarget(target)) return;

      const pastedText = e.clipboardData?.getData("text") ?? "";
      const pasteStart = target.selectionStart ?? 0;
      removeSuggestionHelper();
      removeSuggestionTrigger();
      removeHistoryHelper();
      lastActionWasPaste.current = true;
      pendingTranslationsRef.current.delete(target);

      requestAnimationFrame(async () => {
        if (!target.value) {
          lastActionWasPaste.current = false;
          return;
        }

        try {
          const handledPastedWord = await translatePastedWord({
            target,
            pastedText,
            pasteStart,
          });

          if (handledPastedWord) {
            lastActionWasPaste.current = false;
            return;
          }

          const res = await axios.post(
            API_URL,
            {
              text: target.value,
              source: "en",
              target: "ta",
              mode: "transliterate",
            },
            { headers: { "Content-Type": "application/json" } },
          );

          setNativeValue(target, res.data.translated);
        } catch (err) {
          console.error("Paste translation failed:", err.message);
        }

        lastActionWasPaste.current = false;
      });
    };

    const handleBlur = (e) => {
      const target = e.target;

      if (!isValidTarget(target)) return;

      if (!lastActionWasPaste.current) {
        translateWord(target, true);
      }

      removeSuggestionTrigger();
      removeHistoryHelper();
    };

    const handleDocumentClick = (e) => {
      if (helperRef.current?.contains(e.target)) return;
      if (triggerRef.current?.contains(e.target)) return;
      removeSuggestionHelper();
      removeSuggestionTrigger();
      removeHistoryHelper();
    };

    const handleKeyDown = (e) => {
      const historyHelperState = historyStateRef.current;
      const helperState = suggestionStateRef.current;

      if (historyHelperState && helperState) {
          if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
              activeBoxRef.current = activeBoxRef.current === "suggestion" ? "history" : "suggestion";
              
              if (activeBoxRef.current === "suggestion") {
                  historyHelperState.selectedIndex = -1;
                  helperState.selectedIndex = 0;
              } else {
                  helperState.selectedIndex = -1;
                  historyHelperState.selectedIndex = 0;
              }
              
              historyHelperState.updateHighlight?.();
              helperState.updateHighlight?.();
              
              e.preventDefault();
              e.stopPropagation();
              return;
          }
      }

      if (historyHelperState) {
        if (e.key === "Escape") {
          removeHistoryHelper();
        }
        
        if (e.key === "ArrowDown" || e.key === "ArrowUp") {
          if (activeBoxRef.current === "history" || !helperState) {
              const totalOptions = historyHelperState.suggestions.length;
              if (totalOptions > 0) {
                const step = e.key === "ArrowDown" ? 1 : -1;
                // If it was -1 (inactive), jumping down should go to 0
                let currentIndex = historyHelperState.selectedIndex ?? 0;
                if (currentIndex === -1) currentIndex = 0;
                historyHelperState.selectedIndex = (currentIndex + step + totalOptions) % totalOptions;
                historyHelperState.updateHighlight?.();
                e.preventDefault();
                e.stopPropagation();
                return;
              }
          }
        }
        
        if (e.key === "Enter") {
          if (activeBoxRef.current === "history" || !helperState) {
              const selectedIndex = historyHelperState.selectedIndex ?? 0;
              const item = historyHelperState.suggestions[selectedIndex];
              if (item && selectedIndex !== -1) {
                  applyHistorySuggestion({
                    target: historyHelperState.target,
                    replacementStart: historyHelperState.replacementStart,
                    replacementEnd: historyHelperState.replacementEnd,
                    suggestion: item.tamil
                  });
                  e.preventDefault();
                  e.stopPropagation();
                  return;
              }
          }
        }
        
        if (e.ctrlKey && !e.altKey && !e.metaKey && !e.shiftKey) {
          const m = e.code.match(/Digit([1-5])/);
          if (m) {
            const idx = parseInt(m[1]) - 1;
            const item = historyHelperState.suggestions[idx];
            if (item) {
              applyHistorySuggestion({
                target: historyHelperState.target,
                replacementStart: historyHelperState.replacementStart,
                replacementEnd: historyHelperState.replacementEnd,
                suggestion: item.tamil
              });
              e.preventDefault();
              e.stopPropagation();
              return;
            }
          }
        }
      }

      if (helperState) {
        if (e.key === "Escape") {
          removeSuggestionHelper();
          e.preventDefault();
          e.stopPropagation();
          return;
        }

        // Direct shortcut for Continue with English:
        // 1) Shift + Enter
        // 2) Ctrl + Enter / Cmd + Enter
        // 3) Alt + E
        // 4) Alt + 0
        const isEnglishShortcut =
          helperState.includeOriginalOption &&
          ((e.key === "Enter" && (e.shiftKey || e.ctrlKey || e.metaKey)) ||
           (e.altKey && !e.ctrlKey && !e.metaKey && (e.code === "KeyE" || e.key?.toLowerCase() === "e")) ||
           (e.altKey && !e.ctrlKey && !e.metaKey && !e.shiftKey && (e.code === "Digit0" || e.code === "Numpad0" || e.key === "0")));

        if (isEnglishShortcut) {
          activeEnglishBufferRef.current?.delete(helperState.target);
          applySuggestion({
            target: helperState.target,
            replacementStart: helperState.replacementStart,
            replacementEnd: helperState.replacementEnd,
            suggestion: helperState.originalWord,
            suggestions: helperState.suggestions || [],
            originalWord: helperState.originalWord,
          });
          e.preventDefault();
          e.stopPropagation();
          return;
        }

        // Quick pick for Tamil suggestions with Alt+1-5
        if (e.altKey && !e.ctrlKey && !e.metaKey && !e.shiftKey) {
          const digitMatch = e.code.match(/^(?:Digit|Numpad)([1-9])$/) || (e.key && e.key.match(/^[1-9]$/) ? [null, e.key] : null);
          if (digitMatch) {
            const idx = parseInt(digitMatch[1], 10) - 1;
            if (idx < helperState.suggestions.length) {
              applySuggestion({
                target: helperState.target,
                replacementStart: helperState.replacementStart,
                replacementEnd: helperState.replacementEnd,
                suggestion: helperState.suggestions[idx],
                suggestions: helperState.suggestions,
                originalWord: helperState.originalWord,
              });
              e.preventDefault();
              e.stopPropagation();
              return;
            }
          }
        }

        if (e.key === "ArrowDown" || e.key === "ArrowUp") {
          if (activeBoxRef.current === "suggestion" || !historyHelperState) {
              const totalOptions =
                helperState.suggestions.length +
                (helperState.includeOriginalOption ? 1 : 0);
              if (totalOptions > 0) {
                const step = e.key === "ArrowDown" ? 1 : -1;
                let currentIndex = helperState.selectedIndex ?? 0;
                if (currentIndex === -1) currentIndex = 0;
                helperState.selectedIndex =
                  (currentIndex + step + totalOptions) % totalOptions;
                helperState.updateHighlight?.();
                e.preventDefault();
                e.stopPropagation();
                return;
              }
          }
        }

        if (e.key === "Enter" && !e.shiftKey && !e.ctrlKey && !e.metaKey) {
          if (activeBoxRef.current === "suggestion" || !historyHelperState) {
              const selectedIndex = helperState.selectedIndex ?? 0;
              if (selectedIndex !== -1) {
                  const isEnglishOption =
                    helperState.includeOriginalOption &&
                    selectedIndex === helperState.suggestions.length;
                  const suggestion = isEnglishOption
                    ? helperState.originalWord
                    : helperState.suggestions[selectedIndex];

                  if (isEnglishOption) {
                    activeEnglishBufferRef.current?.delete(helperState.target);
                  }

                  applySuggestion({
                    target: helperState.target,
                    replacementStart: helperState.replacementStart,
                    replacementEnd: helperState.replacementEnd,
                    suggestion:
                      suggestion ||
                      (helperState.includeOriginalOption
                        ? helperState.originalWord
                        : ""),
                    suggestions: helperState.suggestions,
                    originalWord: helperState.originalWord,
                  });
                  e.preventDefault();
                  e.stopPropagation();
                  return;
              }
          }
        }

      }

      const target = e.target;

      if (!isValidTarget(target)) return;

      const isCombobox =
        target.getAttribute("role") === "combobox" ||
        target.getAttribute("aria-autocomplete") === "list";

      if (!isCombobox && !e.altKey && !e.ctrlKey && !e.metaKey && !e.shiftKey && e.key === "ArrowDown") {
        const reopened = reopenSuggestionsAtCursor(target);
        if (!reopened) {
          void translateWord(target, true);
        }

        e.preventDefault();
        e.stopPropagation();
        return;
      }

      if (e.altKey && !e.ctrlKey && !e.metaKey && !e.shiftKey && e.code === "KeyJ") {
        if (!reopenSuggestionsAtCursor(target)) return;

        e.preventDefault();
        e.stopPropagation();
        return;
      }

      const usePrimaryShortcut =
        (e.ctrlKey || e.metaKey) && e.shiftKey && !e.altKey;
      const useFallbackShortcut =
        e.altKey && !e.ctrlKey && !e.metaKey && !e.shiftKey;

      if (!usePrimaryShortcut && !useFallbackShortcut) return;

      if (e.code === "KeyX") {
        if (!cycleTamilLetterVariant(target)) return;

        e.preventDefault();
        e.stopPropagation();
        return;
      }

      const variantIndexByCode = {
        Digit1: 0,
        Numpad1: 0,
        Digit2: 1,
        Numpad2: 1,
        Digit3: 2,
        Numpad3: 2,
      };

      const variantIndex = variantIndexByCode[e.code];
      if (variantIndex === undefined) return;

      const tamilLetter = getTamilLetterAtCursor(
        target.value,
        target.selectionStart ?? 0,
      );
      if (!tamilLetter) return;

      const nextLetter = tamilLetter.variants[variantIndex];
      if (!nextLetter) return;
      if (!replaceTamilLetterVariant(target, nextLetter)) return;

      e.preventDefault();
      e.stopPropagation();
    };

    const handleRequestTamilSuggestion = (e) => {
      const target = e.target;
      if (!target || !isValidTarget(target)) return;
      const selectionEnd = target.selectionEnd ?? target.value.length;
      const textBefore = target.value.slice(0, selectionEnd);
      const textAfter = target.value.slice(selectionEnd);
      const leftMatch = textBefore.match(/[^\s,!?\n-]+$/);
      const rightMatch = textAfter.match(/^[^\s,!?\n-]+/);
      const start = leftMatch ? selectionEnd - leftMatch[0].length : selectionEnd;
      const end = rightMatch ? selectionEnd + rightMatch[0].length : selectionEnd;
      const fullWord = target.value.slice(start, end);
      if (/[a-zA-Z]/.test(fullWord) && fullWord.length > 0) {
        const newBuffer = {
          word: fullWord,
          start,
          tamilLength: fullWord.length,
          expectedCursorPos: selectionEnd,
        };
        activeEnglishBufferRef.current.set(target, newBuffer);
        translateWordFromBuffer(target, newBuffer);
      }
    };

    const handleDismissTamilSuggestions = () => {
      removeSuggestionHelper();
      removeSuggestionTrigger();
      removeHistoryHelper();
    };

    document.addEventListener("paste", handlePaste, true);
    document.addEventListener("input", handleInput);
    document.addEventListener("focusout", handleBlur);
    document.addEventListener("keydown", handleKeyDown, true);
    document.addEventListener("mousedown", handleDocumentClick, true);
    document.addEventListener("request-tamil-suggestion", handleRequestTamilSuggestion);
    document.addEventListener("dismiss-tamil-suggestions", handleDismissTamilSuggestions);
    window.addEventListener("scroll", repositionAllHelpers, true);
    window.addEventListener("resize", repositionAllHelpers);
    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", repositionAllHelpers);
      window.visualViewport.addEventListener("scroll", repositionAllHelpers);
    }

    return () => {
      document.removeEventListener("paste", handlePaste, true);
      document.removeEventListener("input", handleInput);
      document.removeEventListener("focusout", handleBlur);
      document.removeEventListener("keydown", handleKeyDown, true);
      document.removeEventListener("mousedown", handleDocumentClick, true);
      document.removeEventListener("request-tamil-suggestion", handleRequestTamilSuggestion);
      document.removeEventListener("dismiss-tamil-suggestions", handleDismissTamilSuggestions);
      window.removeEventListener("scroll", repositionAllHelpers, true);
      window.removeEventListener("resize", repositionAllHelpers);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener("resize", repositionAllHelpers);
        window.visualViewport.removeEventListener("scroll", repositionAllHelpers);
      }
      pendingTranslations.clear();
      suggestionCacheRef.current = new Map();
      suggestionSourceCacheRef.current = new Map();
      englishOptOutRef.current = new Map();
      fieldSuggestionStateRef.current = new Map();
      removeSuggestionHelper();
      removeSuggestionTrigger();
      removeHistoryHelper();
    };
  }, [isTranslatorEnabled]);
};

export default useGlobalInputListener;
